import { ProdutosData, Departamento, Subdepartamento, Produto as ProdutoData } from '@/resources/Produtos';

// Interface para produto exibido na UI
export interface ProdutoDisplay {
  id: string;
  name: string;
  description: string;
  unit?: string;
  variacoes: string[]; // Array de todas as variações disponíveis
  image: string;
  alt: string;
  category: string;
  subcategory?: string;
  department: string;
  weight?: string;
  badge?: string;
  rating?: number;
  inStock: boolean;
  location?: string;
}

/**
 * Capitaliza a primeira letra de cada palavra
 */
function capitalizarPalavras(texto: string): string {
  if (!texto) return texto;
  return texto
    .toLowerCase()
    .split(' ')
    .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1))
    .join(' ');
}

/**
 * Gera uma descrição de até 100 caracteres baseada no nome do produto
 */
function gerarDescricao(produtoNome: string, maxLength: number = 100): string {
  // Descrições curtas que se adaptam ao tamanho do nome
  const sufixos = [
    '. Produto de alta qualidade para jardinagem.',
    '. Ideal para cultivo e jardinagem.',
    '. Solução profissional para suas plantas.',
    '. Qualidade garantida para seu jardim.',
    '. Produto premium para cultivo.',
    '. Excelente para plantas e jardins.',
    '. Solução completa para jardinagem.',
    '. Produto especializado de qualidade.'
  ];
  
  // Tenta encontrar um sufixo que caiba nos 100 caracteres
  for (const sufixo of sufixos) {
    const descricao = produtoNome + sufixo;
    if (descricao.length <= maxLength) {
      return descricao;
    }
  }
  
  // Se nenhum sufixo couber, usa apenas o nome (limitado a 100 caracteres)
  if (produtoNome.length <= maxLength) {
    return produtoNome;
  }
  
  // Se o nome for muito longo, corta sem reticências
  return produtoNome.substring(0, maxLength).trim();
}

/**
 * Transforma os dados estruturados em produtos individuais para exibição
 * Cada produto mantém todas as suas variações como um array
 */
export function transformarProdutosParaExibicao(
  produtosData: ProdutosData,
  gerarPrecos: boolean = false
): ProdutoDisplay[] {
  const produtos: ProdutoDisplay[] = [];

  produtosData.departamentos.forEach((departamento: Departamento) => {
    departamento.subdepartamentos.forEach((subdepartamento: Subdepartamento) => {
      subdepartamento.produtos.forEach((produto: ProdutoData) => {
        const produtoId = `${departamento.departamento}-${subdepartamento.subdepartamento}-${produto.produto}`.replace(/\s+/g, '-').toLowerCase();
        
        // Capitaliza a primeira letra de cada palavra do nome do produto
        const produtoNomeCapitalizado = capitalizarPalavras(produto.produto);
        
        // Usa descrição específica quando disponível
        const descricao = produto.descricao
          ? produto.descricao
          : gerarDescricao(produtoNomeCapitalizado, 100);
        
        produtos.push({
          id: produtoId,
          name: produtoNomeCapitalizado,
          description: descricao, 
          variacoes: produto.variacoes, // Mantém todas as variações
          image: produto.imagem, // Imagem padrão
          alt: produtoNomeCapitalizado,
          category: subdepartamento.subdepartamento.toUpperCase(),
          subcategory: subdepartamento.subdepartamento.toUpperCase(),
          department: departamento.departamento,
          inStock: true,
          location: 'Brasília - DF'
        });
      });
    });
  });

  // Ordena os produtos alfabeticamente por nome
  produtos.sort((a, b) => {
    const nomeA = a.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const nomeB = b.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return nomeA.localeCompare(nomeB, 'pt-BR');
  });

  return produtos;
}

/**
 * Gera um preço aleatório para demonstração
 * Em produção, isso viria de uma API ou banco de dados
 */
function gerarPrecoAleatorio(): number {
  return Math.floor(Math.random() * 200) + 10;
}

/**
 * Extrai todas as categorias únicas (subdepartamentos) dos dados
 */
export function extrairCategorias(produtosData: ProdutosData): string[] {
  const categorias = new Set<string>();
  
  produtosData.departamentos.forEach((departamento: Departamento) => {
    departamento.subdepartamentos.forEach((subdepartamento: Subdepartamento) => {
      categorias.add(subdepartamento.subdepartamento);
    });
  });
  
  return ['Todos os Produtos', ...Array.from(categorias).sort().map(cat => cat.toUpperCase())];
}

/**
 * Adiciona prefixos às categorias conforme especificado
 */
export function formatarCategoriaComPrefixo(categoria: string, departamento: string): string {
  if (categoria === 'Todos os Produtos') return categoria;
  
  // Sementes
  const sementesCategorias = [
    'HORTALIÇAS EM GERAL', 'TOMATES', 'FRUTAS', 'ALFACES', 
    'ERVAS E TEMPEROS', 'PIMENTAS', 'ABÓBORAS', 'PARA GATOS'
  ];
  if (sementesCategorias.includes(categoria.toUpperCase())) {
    return `Sementes de ${categoria}`;
  }
  
  // Adubos químicos
  const adubosQuimicosCategorias = [
    'PARA SOLO', 'SOLÚVEIS', 'CORRETIVOS DE SOLO', 'SAIS', 'LIBERAÇÃO LENTA'
  ];
  if (adubosQuimicosCategorias.includes(categoria.toUpperCase())) {
    return `Adubos químicos ${categoria}`;
  }
  
  // Adubo orgânico
  const aduboOrganicoCategorias = [
    'ORIGEM ANIMAL', 'ORIGEM VEGETAL', 'COMPOSTOS (combinados)'
  ];
  if (aduboOrganicoCategorias.includes(categoria.toUpperCase())) {
    return `Adubo orgânico ${categoria}`;
  }
  
  // Substratos
  const substratosCategorias = ['SIMPLES (OU PUROS)', 'COMPOSTOS (OU COMBINADOS)'];
  if (substratosCategorias.includes(categoria.toUpperCase())) {
    return `Substratos ${categoria}`;
  }
  
  return categoria;
}

/**
 * Extrai categorias filtradas por departamento
 */
export function extrairCategoriasPorDepartamento(
  produtosData: ProdutosData, 
  departamentoSelecionado: string
): string[] {
  const categorias = new Set<string>();
  
  produtosData.departamentos.forEach((departamento: Departamento) => {
    if (departamentoSelecionado === 'Todos os Departamentos' || 
        departamento.departamento === departamentoSelecionado) {
      departamento.subdepartamentos.forEach((subdepartamento: Subdepartamento) => {
        categorias.add(subdepartamento.subdepartamento);
      });
    }
  });
  
  return ['Todos os Produtos', ...Array.from(categorias).sort().map(cat => cat.toUpperCase())];
}

/**
 * Extrai todos os departamentos únicos
 */
export function extrairDepartamentos(produtosData: ProdutosData): string[] {
  const departamentos = new Set<string>();
  
  produtosData.departamentos.forEach((departamento: Departamento) => {
    departamentos.add(departamento.departamento);
  });
  
  return ['Todos os Departamentos', ...Array.from(departamentos).sort()];
}

/**
 * Extrai todas as variações/medidas únicas dos produtos
 */
export function extrairVariacoes(produtosData: ProdutosData): string[] {
  const variacoes = new Set<string>();
  
  produtosData.departamentos.forEach((departamento: Departamento) => {
    departamento.subdepartamentos.forEach((subdepartamento: Subdepartamento) => {
      subdepartamento.produtos.forEach((produto: ProdutoData) => {
        produto.variacoes.forEach((variacao: string) => {
          variacoes.add(variacao);
        });
      });
    });
  });
  
  return ['Todas as Grandezas', ...Array.from(variacoes).sort()];
}

